import * as Sentry from '@sentry/react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import { MdContentCopy, MdDelete, MdNotes, MdEmail, MdPerson, MdChevronRight, MdSensors, MdChat } from "react-icons/md"
import { appLabels } from '../../../constants'
import http from '../../../lib/http'
import { hasAdminRole } from '../../../selectors/user'
import * as Table from '../../claim-history/simple-table'
import { Copyable } from "../../layout/sidebar"
import { ContextMenuButton } from '../../shared/ContextMenu'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'
import { DiagnosticEmailData, DiagnosticEmailModal } from './diagnostic-email-modal'
import { useAppDispatch, useAppSelector } from '../../../store'
import { notify } from '../../../lib/Notification/actions'

interface Group {
  number: string
  name: string
}

interface GroupsApiResponse {
  ok: boolean

  data: {
    groups: Group[]

    meta: {
      total: number
    }
  }
}

interface ExpandingComponentProps {
  item: CompassionUser
  onAction?: () => void
}

const platformMapper: Record<string, string> = {
	ios: "iOS",
	android: "Android",
};

function ExpandingComponent({ item, onAction = () => {} }: ExpandingComponentProps): JSX.Element {
	const [devicesExpanded, setDevicesExpanded] = useState(false);

	const appDispatch = useAppDispatch();

	const role = useAppSelector(
		(state) => state?.auth?.profile?.app_metadata?.roles?.meta?.role ?? null,
	);

	const isAdmin = [
		"administrator",
		"executive_director",
		"medical_director",
	].includes(role ?? "");

	const mobileManagement: string = useAppSelector(
		(state) => state?.globalVars?.permissions?.mobile_management ?? "none",
	);

	const hasAccessToActions = isAdmin || mobileManagement === "write";

  const toggleDevicesExpanded = () => {
    setDevicesExpanded(!devicesExpanded)
  }

  return (
    <div className={styles.expandableRowContent}>
      <div className={styles.row}>
        <div className={styles.details}>
          <span className={styles.label}>Name</span>
          <span className={styles.value}>
            {item.name || '—'}
          </span>

          <span className={styles.label}>Email</span>
          <span className={styles.value}>
            {typeof item.email === 'string' && (
              <Copyable value={item.email}>
                {item.email}
              </Copyable>
            )}

            {typeof item.email !== 'string' && '—'}
          </span>

          <span className={styles.label}>Phone</span>
          <span className={styles.value}>
            {typeof item.phone === 'string' && (
              <Copyable value={item.phone}>
                {item.phone}
              </Copyable>
            )}

            {typeof item.phone !== 'string' && '—'}
          </span>

          <span className={styles.label}>ID</span>
          <span className={styles.value}>
            <Copyable value={item.id}>
              {item.id}
            </Copyable>
          </span>

          <span className={styles.label}>Created</span>
          <span className={styles.value}>
            {typeof item.created_at === 'string' && Table.formatTime(item.created_at)}
            {typeof item.created_at !== 'string' && '—'}
          </span>

          <span className={styles.label}>Updated</span>
          <span className={styles.value}>
            {typeof item.updated_at === 'string' && Table.formatTime(item.updated_at)}
            {typeof item.updated_at !== 'string' && '—'}
          </span>

          <span className={styles.label}>Accepted TOS</span>
          <span className={styles.value}>
            {(item.tos?.length ?? 0) === 0 && 'Not accepted'}

            {(item.tos?.length ?? 0) > 0 && (
              <div
                className={styles.details}
                style={{ gap: '0.75rem 0.625rem' }}
              >
                {item.tos?.map(tos => (
                  <Fragment key={tos.accepted_at}>
                    {typeof tos.version === 'string' && (
                      <span
                        className={styles.version}
                        title={`TOS version ${tos.version} (${tos.app})`}
                      >
                        {tos.version}
                      </span>
                    )}

                    {typeof tos.version !== 'string' && (
                      <div />
                    )}

                    <span>
                      {typeof tos.accepted_at === 'string' && Table.formatTime(tos.accepted_at)}
                      {typeof tos.accepted_at !== 'string' && '—'}
                    </span>
                  </Fragment>
                ))}
              </div>
            )}
          </span>
        </div>
      </div>

      {(item.devices?.length ?? 0) > 0 && (
        <div className={styles.c}>
          <div className={styles.r}>
            <button
              type='button'
              className={styles.collapseButton}
              onClick={toggleDevicesExpanded}
            >
              <span className={styles.sectionHeader}>
                Devices
              </span>

              <MdChevronRight
                size={20}
                style={{
                  transform: `rotate(${devicesExpanded ? 90 : 0}deg) translateY(1px)`
                }}
              />
            </button>
          </div>

          {devicesExpanded && (
            <div
              className={styles.c}
              style={{ marginTop: '1rem', gap: '1rem' }}
            >
              <div className={styles.details}>
                <span
                  className={styles.label}
                  style={{ minWidth: '55px' }}
                >
                  All
                </span>
                <span className={styles.value}>
                  {platformMapper.ios}: {item.devices?.filter(device => device.platform === 'ios').length ?? 0}

                  <span style={{ padding: '0 0.5rem' }}>
                    •
                  </span>

                  {platformMapper.android}: {item.devices?.filter(device => device.platform === 'android').length ?? 0}

                  {hasAccessToActions && (
                    <div className={styles.actionButtons}>
                      <button
                        type='button'
                        className={styles.actionButton}
                        onClick={() => {
                          http.postRequest('/admin/mobile/ping', {
                            id: item.metadataId
                          })
                            .then(() => {
                              appDispatch(notify('Devices pinged', false))
                            })
                            .catch((err: unknown) => {
                              console.error(err)
                              Sentry.captureException(err)
                              appDispatch(notify('Failed to ping devices', true))
                            })
                        }}
                        title='Ping all devices'
                      >
                        <MdSensors
                          size={20}
                          color='#fff'
                        />
                      </button>

                      <button
                        type='button'
                        className={styles.actionButton}
                        onClick={() => {
                          http.postRequest('/admin/mobile/messages', {
                            id: item.metadataId
                          })
                            .then(() => {
                              appDispatch(notify('Messages notifications sent', false))
                            })
                            .catch((err: unknown) => {
                              console.error(err)
                              Sentry.captureException(err)
                              appDispatch(notify('Failed to send messages notifications', true))
                            })
                        }}
                        title='Send message notification to all devices'
                      >
                        <MdChat
                          size={20}
                          color='#fff'
                          style={{
                            transform: 'scale(0.8) translateY(1px)'
                          }}
                        />
                      </button>

                      <button
                        type='button'
                        className={styles.actionButton}
                        style={{
                          backgroundColor: '#d0143a'
                        }}
                        onClick={() => {
                          let confirmed = window.confirm('Are you sure you want to delete all devices?')

                          if (!confirmed) {
                            return
                          }

                          http.postRequest('/admin/mobile/remove', {
                            id: item.metadataId
                          })
                            .then(() => {
                              onAction?.()
                              appDispatch(notify('All devices deleted', false))
                            })
                            .catch((err: unknown) => {
                              console.error(err)
                              Sentry.captureException(err)
                              appDispatch(notify('Failed to delete all device', true))
                            })
                        }}
                        title='Delete all devices'
                      >
                        <MdDelete
                          size={20}
                          color='#fff'
                          style={{
                            transform: 'scale(0.95)'
                          }}
                        />
                      </button>
                    </div>
                  )}
                </span>
              </div>

              <hr />

              {item.devices?.filter(Boolean).map((device, i, a) => (
                <Fragment key={device.arn}>
                  <div className={styles.details}>
                    <span className={styles.label}>Platform</span>
                    <span className={styles.value}>
                      {platformMapper[device.platform ?? ''] ?? device.platform ?? '—'}

                      {hasAccessToActions && (
                        <div
                          className={styles.actionButtons}
                          style={{
                            paddingLeft: device.platform === 'ios'
                              ? '7.325rem'
                              : '5.7125rem'
                          }}
                        >
                          <button
                            type='button'
                            className={styles.actionButton}
                            onClick={() => {
                              http.postRequest('/admin/mobile/ping', {
                                id: item.metadataId,
                                device
                              })
                                .then(() => {
                                  appDispatch(notify('Device pinged', false))
                                })
                                .catch((err: unknown) => {
                                  console.error(err)
                                  Sentry.captureException(err)
                                  appDispatch(notify('Failed to ping device', true))
                                })
                            }}
                            title='Ping this device'
                          >
                            <MdSensors
                              size={20}
                              color='#fff'
                            />
                          </button>

                          <button
                            type='button'
                            className={styles.actionButton}
                            onClick={() => {
                              http.postRequest('/admin/mobile/messages', {
                                id: item.metadataId,
                                device
                              })
                                .then(() => {
                                  appDispatch(notify('Messages notification sent', false))
                                })
                                .catch((err: unknown) => {
                                  console.error(err)
                                  Sentry.captureException(err)
                                  appDispatch(notify('Failed to send messages notification', true))
                                })
                            }}
                            title='Send message notification to this device'
                          >
                            <MdChat
                              size={20}
                              color='#fff'
                              style={{
                                transform: 'scale(0.8) translateY(1px)'
                              }}
                            />
                          </button>

                          <button
                            type='button'
                            className={styles.actionButton}
                            style={{
                              backgroundColor: '#d0143a'
                            }}
                            onClick={() => {
                              let confirmed = window.confirm('Are you sure you want to delete this device?')

                              if (!confirmed) {
                                return
                              }

                              http.postRequest('/admin/mobile/remove', {
                                id: item.metadataId,
                                device
                              })
                                .then(() => {
                                  onAction?.()
                                  appDispatch(notify('Device deleted', false))
                                })
                                .catch((err: unknown) => {
                                  console.error(err)
                                  Sentry.captureException(err)
                                  appDispatch(notify('Failed to delete device', true))
                                })
                            }}
                            title='Delete this device'
                          >
                            <MdDelete
                              size={20}
                              color='#fff'
                              style={{
                                transform: 'scale(0.95)'
                              }}
                            />
                          </button>
                        </div>
                      )}
                    </span>

                    <span className={styles.label}>Token</span>
                    <span
                      className={styles.value}
                      style={{ overflow: 'hidden' }}
                    >
                      {typeof device.token === 'string' && (
                        <Copyable
                          value={device.token}
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {device.token}
                        </Copyable>
                      )}

                      {typeof device.token !== 'string' && '—'}
                    </span>

                    <span className={styles.label}>ARN</span>
                    <span className={styles.value}>
                      {typeof device.arn === 'string' && (
                        <Copyable value={device.arn}>
                          {device.arn}
                        </Copyable>
                      )}

                      {typeof device.arn !== 'string' && '—'}
                    </span>

                    <span className={styles.label}>Time</span>
                    <span className={styles.value}>
                      {typeof device.time === 'string' && Table.formatTime(device.time)}
                      {typeof device.time !== 'string' && '—'}
                    </span>

                    <span className={styles.label}>Locale</span>
                    <span className={styles.value}>
                      {device.locale ?? '—'}
                    </span>
                  </div>

                  {i < a.length - 1 && (
                    <hr />
                  )}
                </Fragment>
              ))}
            </div>
          )}

        </div>
      )}
    </div>
  )
}

export interface CompassionUser extends Table.Item {
  id: string;
  email: string;
  name: string;
  created_at: string;
  updated_at: string;
  group: Group;
  last_seen_at: string | undefined;
  application: "lucent-health" | "communicare" | null;

  patient: {
    mid: string | undefined;
    member_id: string | undefined;
  };

  tos: Array<{
    id: string;
    version: number;
    accepted_at: string;
    app: string;
  }>;

  devices?: Device[] | null
  metadataId?: string | null
}

interface Device {
  arn?: string | null
  time?: string | null
  token?: string | null
  locale?: string | null
  platform?: string | null
}

export interface CompassionUsersResponse {
  data: {
    users: CompassionUser[];
    meta: {
      total: number;
    };
  };
}

const clearButtonStyle: React.CSSProperties = {
  color: '#7826f5',
  backgroundColor: 'rgba(220, 197, 249, 0.5)',
  backdropFilter: 'blur(9px)',

  textTransform: 'uppercase',
  fontSize: '0.75rem',
  fontWeight: '600',
  textAlign: 'center',

  padding: '0.5rem',
  width: '100%',

  borderBottomLeftRadius: '1rem',
  borderBottomRightRadius: '1rem',

  cursor: 'pointer'
}

function formatDate(isoString: string): string {
  try {
    const date = new Date(isoString);

    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    return `${dateFormatter.format(date)} ${timeFormatter.format(date)}`;
  } catch (e) {
    return isoString;
  }
}

export function PortalMobileUsers(): JSX.Element {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const isAdmin = useSelector(hasAdminRole)

  const [page, setPage] = useState(0) // 0-based
  const [pageSize, setPageSize] = useState(10)
  const [sort, setSort] = useState<Table.Sort<CompassionUser>>({ field: 'created_at', direction: 'desc' })

  const [emailFilter, setEmailFilter] = useState<string | undefined>(undefined)
  const [memberIdFilter, setMemberIdFilter] = useState<string | undefined>(undefined)
  const [groupsFilter, setGroupsFilter] = useState<string[]>([])
  const [tosDateFilter, setTosDateFilter] = useState<string | undefined>(undefined)
  const [applicationFilter, setApplicationFilter] = useState<string[]>([])
  const [createdAtFilter, setCreatedAtFilter] = useState<string | undefined>(undefined)
  const [updatedAtFilter, setUpdatedAtFilter] = useState<string | undefined>(undefined)
  const [nameFilter, setNameFilter] = useState<string | undefined>(undefined)
  const [queryFilter, setQueryFilter] = useState<string | undefined>(undefined)

  const [debouncedEmail] = useDebounceValue(emailFilter, 600)
  const [debouncedMemberId] = useDebounceValue(memberIdFilter, 600)
  const [debouncedTosDate] = useDebounceValue(tosDateFilter, 300)
  const [debouncedCreatedAt] = useDebounceValue(createdAtFilter, 300)
  const [debouncedUpdatedAt] = useDebounceValue(updatedAtFilter, 300)
  const [debouncedName] = useDebounceValue(nameFilter, 600)
  const [debouncedQuery] = useDebounceValue(queryFilter, 600)

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

  const { data, status, error } = useQuery<CompassionUsersResponse>({
    queryKey: [
      "portal-mobile-users",
      {
        page,
        pageSize,
        sort,
        email: debouncedEmail,
        memberId: debouncedMemberId,
        groups: groupsFilter,
        tosDate: debouncedTosDate,
        application: applicationFilter,
        createdAt: debouncedCreatedAt,
        updatedAt: debouncedUpdatedAt,
        nameFilter: debouncedName,
        query: debouncedQuery
      },
    ],
    queryFn: async () => {
      const searchParams = new URLSearchParams([
        ["page", (page + 1).toString()],
        ["pageSize", pageSize.toString()],
      ]);

      if (sort?.field) {
        searchParams.set('sort', `${sort.direction === 'asc' ? '' : '-'}${sort.field as string}`)
      }

      if (debouncedEmail?.trim()) {
        searchParams.set("email", debouncedEmail.trim());
      }

      if (debouncedMemberId?.trim()) {
        searchParams.set("memberId", debouncedMemberId.trim());
      }

      groupsFilter.forEach(groupNumber => {
        searchParams.append('group', String(groupNumber))
      })

      if (debouncedTosDate?.trim()) {
        searchParams.set("tosDate", debouncedTosDate.trim());
      }

      applicationFilter.forEach(application => {
        searchParams.append('application', application)
      })

      if (debouncedCreatedAt?.trim()) {
        searchParams.set("createdAt", debouncedCreatedAt.trim());
      }

      if (debouncedUpdatedAt?.trim()) {
        searchParams.set("updatedAt", debouncedUpdatedAt.trim());
      }

      if (debouncedName?.trim()) {
        searchParams.set('name', debouncedName.trim())
      }

      if (debouncedQuery?.trim()) {
        searchParams.set('query', debouncedQuery.trim())
      }

      const response = await http.getRequest<CompassionUsersResponse>(
        `/admin/portal-mobile-users?${searchParams.toString()}`
      );

      return response;
    },

    placeholderData: keepPreviousData,
  });

  const deleteTos = useMutation({
    mutationFn: async (tosAcceptanceId: string) => {
      await http.deleteRequest(`/admin/portal-mobile-users/tos/${tosAcceptanceId}`);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["portal-mobile-users"] });
      toast.success("Last accepted ToS was deleted successfully", { position: 'top-right' });
    },

    onError: (error: any) => {
      toast.error(error?.message || "Failed to delete TOS acceptance", { position: 'top-right' });
    },
  });

  const sendDiagnosticEmail = useMutation({
    mutationFn: async (data: DiagnosticEmailData) => {
      await http.postRequest('/admin/portal-mobile-users/diagnostic-email', data);
    },

    onSuccess: () => {
      toast.success('Diagnostic email sent successfully', { position: 'top-right' });
      setIsEmailModalOpen(false);
    },

    onError: (error: any) => {
      toast.error(error?.message || 'Failed to send diagnostic email', { position: 'top-right' });
    },
  });

  const columns = useMemo<Array<Table.Column<CompassionUser>>>(
    () => [
      {
        name: "id",
        header: "ID",
        gridArea: "130px",
        getValue: (row) => {
          const firstIdPart = row.id.slice(0, 4);
          const lastIdPart = row.id.slice(-4);

          return (
            <div className="flex gap-2 justify-between items-center">
              {`${firstIdPart}...${lastIdPart}`}

              <Copyable
                className="flex justify-center items-center"
                notificationOptions={{
                  position: "top-right",
                  duration: 2000,
                }}
                value={row.id}
              >
                <MdContentCopy size={14} className="text-[#666666]" />
              </Copyable>
            </div>
          );
        },
      },
      {
        name: "email",
        header: "Email",
      },
      {
        name: 'name',
        header: 'Name',
        gridArea: 'max-content',
        isSortable: true,
        getValue: (row) => {
          if (!row.name) {
            return <span style={{ color: '#9ca3af' }}>N/A</span>
          }

          return row.name;
        }
      },
      {
        name: "member_id",
        header: "Member ID",
        gridArea: "max-content",
        getValue: (row) => {
          const memberId = row.patient?.member_id;

          if (!memberId) {
            return <span style={{ color: '#9ca3af' }}>N/A</span>
          }

          return memberId;
        }
      },
      {
        name: 'group',
        header: 'Group',
        gridArea: 'max-content',
        getValue: (row) => {
          const groupNumber = row.group.number
          const groupName = row.group.name

          if (!groupNumber || !groupName) {
            return <span style={{ color: '#9ca3af' }}>N/A</span>
          }

          return (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap'
            }}>
              {groupNumber && (
                <span style={{ fontWeight: '500' }}>{groupNumber}</span>
              )}

              {groupName && (
                <span style={{ color: '#9ca3af' }}>{groupName}</span>
              )}
            </div>
          )
        }
      },
      {
        name: "tos_accepted",
        header: "Accepted ToS",
        gridArea: "max-content",
        getValue: (row) => {
          if (!row.tos || row.tos.length === 0) {
            return <span style={{ color: '#9ca3af' }}>N/A</span>;
          }

          const latestTos = row.tos.sort((a, b) =>
            new Date(b.accepted_at).getTime() - new Date(a.accepted_at).getTime()
          )[0];

          if (!latestTos) {
            return <span style={{ color: '#9ca3af' }}>N/A</span>;
          }

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {formatDate(latestTos.accepted_at)}

              <div style={{ display: "flex", gap: "6px", fontSize: "0.75rem", color: "#666" }}>
                <span>v{latestTos.version}</span>
                <span>•</span>
                <span>{appLabels[latestTos.app] || latestTos.app}</span>
              </div>
            </div>
          );
        },
      },
      {
        name: "application",
        header: "Application",
        gridArea: "max-content",
        getValue: (row) => {
          if (!row.application) {
            return <span style={{ color: '#9ca3af' }}>N/A</span>
          }

          const label = appLabels[row.application] || row.application;
          const badgeStyle = row.application === 'lucent-health'
            ? {
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              color: '#1e40af'
            }
            : row.application === 'communicare'
              ? {
                backgroundColor: 'rgba(249, 115, 22, 0.1)',
                color: '#c2410c'
              }
              : {
                backgroundColor: 'rgba(107, 114, 128, 0.1)',
                color: '#374151'
              };

          return (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0.125rem 0.5rem',
                borderRadius: '0.25rem',
                fontSize: '0.75rem',
                fontWeight: '500',
                width: '100%',
                ...badgeStyle
              }}
            >
              {label}
            </span>
          );
        },
      },
      {
        name: "created_at",
        header: "Created",
        isSortable: true,
        initialSortDirection: "desc",
        gridArea: "max-content",
        getValue: (row) => {
          return formatDate(row.created_at)
        }
      },
      {
        name: "updated_at",
        header: "Updated",
        isSortable: true,
        gridArea: "max-content",
        getValue: (row) => {
          return formatDate(row.updated_at)
        }
      },
      {
        name: "last_seen_at",
        header: "Last Seen",
        isSortable: true,
        gridArea: "max-content",
        getValue: (row) => {
          if (!row.last_seen_at) {
            return <span style={{ color: '#9ca3af' }}>N/A</span>
          }

          return formatDate(row.last_seen_at)
        }
      },
      {
        name: 'actions',
        header: null,
        gridArea: '50px',
        getValue: (row) => {
          const latestTos = row.tos && row.tos.length > 0
            ? row.tos.sort((a, b) =>
                new Date(b.accepted_at).getTime() - new Date(a.accepted_at).getTime()
              )[0]
            : null;

          const actions = [
            {
              id: 'view-matching-attempts',
              icon: MdNotes,
              label: 'View matching attempts',
              onClick: () => {
                const params = new URLSearchParams({ query: row.email })
                const baseUrl = '/admin/profile-matching'
                navigate(row.email ? `${baseUrl}?${params}` : baseUrl)
              }
            }
          ];

          if (row.patient?.member_id) {
            actions.push({
              id: 'view-patient-profile',
              icon: MdPerson,
              label: 'View patient profile',
              onClick: () => {
                const params = new URLSearchParams({ query: row.patient?.member_id ?? ''})
                const baseUrl = '/admin/portal-index'
                navigate(row.patient?.member_id ? `${baseUrl}?${params}` : baseUrl)
              }
            });
          }

          if (latestTos && isAdmin) {
            actions.push({
              id: 'delete-tos',
              icon: MdDelete,
              label: 'Delete last accepted ToS',
              onClick: () => {
                if (window.confirm("Are you sure you want to delete this TOS acceptance?")) {
                  deleteTos.mutate(latestTos.id);
                }
              }
            });
          }

          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
              <ContextMenuButton
                actions={actions}
                onClose={() => ({})}
              />
            </div>
          );
        }
      }
    ],
    [deleteTos, isAdmin]
  );

  const pagination = useMemo(() => ({
    page,
    pageSize,
    totalFiltered: data?.data?.meta?.total ?? 0,
    totalUnfiltered: data?.data?.meta?.total ?? 0,
  }), [page, pageSize, data?.data?.meta?.total])

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>Mobile Users</h1>

        <SearchInput
          value={queryFilter}
          placeholder="Search by user info"
          onChange={(e) => {
            setQueryFilter(e.target.value);
            setPage(0);
          }}
        />

        <button
          onClick={() => setIsEmailModalOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1rem',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2563eb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#3b82f6';
          }}
        >
          <MdEmail size={18} />
          Send Test Email
        </button>
      </PageHeader>

      <DiagnosticEmailModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSend={(data) => sendDiagnosticEmail.mutate(data)}
        isLoading={sendDiagnosticEmail.isPending}
      />

      <Table.Table
        data={data?.data?.users ?? []}
        queryStatus={status}
        error={error as Error | undefined}
        noDataPlaceholder="No users to display"
        columns={columns}
        sort={sort}
        onSortChange={(field, direction) =>
          setSort({ field: field as keyof CompassionUser, direction })
        }
        pagination={pagination}
        onPageChange={(value: number) => setPage(value - 1)}
        onPageSizeChange={(value: number) => {
          setPageSize(value);
          setPage(0);
        }}
        expandingComponent={(item, _state, _setState) => {
          return (
            <ExpandingComponent item={item} />
          )
        }}
        filters={[
          {
            name: 'email',
            value: emailFilter ? [emailFilter] : [],
            render: ({ onChange, value = [] }) => {
              const currentValue = (value?.[0] as string) || ''

              return (
                <div style={{ padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', borderBottom: '1px solid #eaeaea' }}>
                  <input
                    autoFocus
                    type='text'
                    value={currentValue}
                    placeholder='Email'
                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                    style={{ width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd' }}
                  />
                </div>
              )
            },
            clearButton: ({ onChange, value = [] }) => {
              const currentValue = value?.[0] as string

              if (!currentValue || currentValue.trim() === '') {
                return null
              }

              return (
                <button
                  type='button'
                  style={clearButtonStyle}
                  onClick={() => {
                    onChange([])
                  }}
                >
                  Clear filter
                </button>
              )
            },
            isActive: emailFilter !== undefined && emailFilter.trim() !== ''
          },
          {
            name: 'name',
            value: nameFilter ? [nameFilter] : [],
            render: ({ onChange, value = [] }) => {
              const currentValue = (value?.[0] as string) || ''

              return (
                <div style={{ padding: '0.5rem' }}>
                  <input
                    autoFocus
                    type='text'
                    value={currentValue}
                    placeholder='Name'
                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                    style={{ width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd' }}
                  />
                </div>
              )
            },
            clearButton: ({ onChange, value = [] }) => {
              const currentValue = value?.[0] as string

              if (!currentValue || currentValue.trim() === '') {
                return null
              }

              return (
                <button
                  type='button'
                  style={clearButtonStyle}
                  onClick={() => {
                    onChange([])
                  }}
                >
                  Clear filter
                </button>
              )
            },
            isActive: nameFilter !== undefined && nameFilter.trim() !== ''
          },
          {
            name: 'member_id',
            value: memberIdFilter ? [memberIdFilter] : [],
            render: ({ onChange, value = [] }) => {
              const currentValue = (value?.[0] as string) || ''

              return (
                <div style={{ padding: '0.5rem' }}>
                  <input
                    autoFocus
                    type='text'
                    value={currentValue}
                    placeholder='Member ID'
                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                    style={{ width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd' }}
                  />
                </div>
              )
            },
            clearButton: ({ onChange, value = [] }) => {
              const currentValue = value?.[0] as string

              if (!currentValue || currentValue.trim() === '') {
                return null
              }

              return (
                <button
                  type='button'
                  style={clearButtonStyle}
                  onClick={() => {
                    onChange([])
                  }}
                >
                  Clear filter
                </button>
              )
            },
            isActive: memberIdFilter !== undefined && memberIdFilter.trim() !== ''
          },
          {
            name: 'group',
            value: groupsFilter,
            render: ({ onChange, value = [] }) => <GroupsFilter onChange={onChange} value={value as string[]} />,
            clearButton: ({ onChange, value = [] }) => {
              const currentValue = value as string[]

              if (!currentValue || currentValue.length === 0) {
                return null
              }

              return (
                <button
                  type='button'
                  style={clearButtonStyle}
                  onClick={() => {
                    onChange([])
                  }}
                >
                  Clear filter
                </button>
              )
            },
            isActive: groupsFilter.length > 0
          },
          {
            name: "application",
            value: applicationFilter,
            hideSearch: true,
            options: [
              { value: "lucent-health", label: "Lucent Health" },
              { value: "communicare", label: "My Health 365" },
            ],
          },
          {
            name: 'created_at',
            value: createdAtFilter ? [createdAtFilter] : [],
            render: ({ onChange, value = [] }) => {
              const currentValue = (value?.[0] as string) || ''

              return (
                <div style={{ padding: '0.5rem' }}>
                  <input
                    autoFocus
                    type='date'
                    lang='en-US'
                    value={currentValue}
                    placeholder='Created At'
                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                    style={{ width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd' }}
                  />
                </div>
              )
            },
            clearButton: ({ onChange, value = [] }) => {
              const currentValue = value?.[0] as string

              if (!currentValue || currentValue.trim() === '') {
                return null
              }

              return (
                <button
                  type='button'
                  style={clearButtonStyle}
                  onClick={() => {
                    onChange([])
                  }}
                >
                  Clear filter
                </button>
              )
            },
            isActive: createdAtFilter !== undefined && createdAtFilter.trim() !== ''
          },
          {
            name: 'updated_at',
            value: updatedAtFilter ? [updatedAtFilter] : [],
            render: ({ onChange, value = [] }) => {
              const currentValue = (value?.[0] as string) || ''

              return (
                <div style={{ padding: '0.5rem' }}>
                  <input
                    autoFocus
                    type='date'
                    lang='en-US'
                    value={currentValue}
                    placeholder='Updated At'
                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                    style={{ width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd' }}
                  />
                </div>
              )
            },
            clearButton: ({ onChange, value = [] }) => {
              const currentValue = value?.[0] as string

              if (!currentValue || currentValue.trim() === '') {
                return null
              }

              return (
                <button
                  type='button'
                  style={clearButtonStyle}
                  onClick={() => {
                    onChange([])
                  }}
                >
                  Clear filter
                </button>
              )
            },
            isActive: updatedAtFilter !== undefined && updatedAtFilter.trim() !== ''
          },
          {
            name: 'tos_accepted',
            value: tosDateFilter ? [tosDateFilter] : [],
            render: ({ onChange, value = [] }) => {
              const currentValue = (value?.[0] as string) || ''

              return (
                <div style={{ padding: '0.5rem' }}>
                  <input
                    autoFocus
                    type='date'
                    lang='en-US'
                    value={currentValue}
                    placeholder='ToS Accepted At'
                    onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
                    style={{ width: 260, padding: '0.5rem', borderRadius: 8, border: '1px solid #ddd' }}
                  />
                </div>
              )
            },
            clearButton: ({ onChange, value = [] }) => {
              const currentValue = value?.[0] as string

              if (!currentValue || currentValue.trim() === '') {
                return null
              }

              return (
                <button
                  type='button'
                  style={clearButtonStyle}
                  onClick={() => {
                    onChange([])
                  }}
                >
                  Clear filter
                </button>
              )
            },
            isActive: tosDateFilter !== undefined && tosDateFilter.trim() !== ''
          }
        ]}
        onFilterChange={(field, values) => {
          const currentValue = values?.[0] as string;

          if (field === "email") {
            setEmailFilter(currentValue);
          }

          if (field === "member_id") {
            setMemberIdFilter(currentValue);
          }

          if (field === 'group') {
            setGroupsFilter(values as string[])
          }

          if (field === "tos_accepted") {
            setTosDateFilter(currentValue);
          }

          if (field === "application") {
            setApplicationFilter(values as string[]);
          }

          if (field === "created_at") {
            setCreatedAtFilter(currentValue);
          }

          if (field === "updated_at") {
            setUpdatedAtFilter(currentValue);
          }

          if (field === "name") {
            setNameFilter(currentValue);
          }

          setPage(0);
        }}
        onFiltersClear={() => {
          setEmailFilter(undefined);
          setMemberIdFilter(undefined);
          setGroupsFilter([])
          setApplicationFilter([])
          setTosDateFilter(undefined);
          setCreatedAtFilter(undefined);
          setUpdatedAtFilter(undefined);
          setNameFilter(undefined);

          setPage(0);
        }}
      />
    </div>
  );
}

const GroupsFilter = (props: { onChange: (value: string[]) => void, value: string[] }) => {
  const [groupSearchQuery, setGroupSearchQuery] = useState<string>('')
  const [debouncedGroupSearch] = useDebounceValue(groupSearchQuery, 400)

  const { data: groupsData, isPending } = useQuery<GroupsApiResponse>({
    queryKey: ['lucent-index-groups', debouncedGroupSearch?.trim() || ''],
    queryFn: async () => {
      const searchParams = new URLSearchParams()

      if (debouncedGroupSearch?.trim()) {
        searchParams.set('query', debouncedGroupSearch.trim())
      }

      return http.getRequest<GroupsApiResponse>(
        `/admin/portal-index/groups?${searchParams.toString()}`
      )
    },

    enabled: true,
    staleTime: 5 * 60 * 1000,
  })

  const groups = groupsData?.data?.groups ?? []

  const checkboxStyle = `
      .groups-filter-checkbox {
          position: relative;
      }
      .groups-filter-checkbox::before {
          content: "";
          width: 0.55rem;
          height: 0.55rem;
          transform: scale(0);
          box-shadow: inset 1rem 1rem #fff;
          transform-origin: center center;
          clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
          position: absolute;
          top: 50%;
          left: 50%;
          margin-top: -0.275rem;
          margin-left: -0.275rem;
          transition: transform 100ms ease-in-out;
      }
      .groups-filter-checkbox:checked::before {
          transform: scale(1);
      }
  `

  return (
    <>
      <style>{checkboxStyle}</style>
      <div style={{ width: '400px' }}>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          backgroundColor: '#fff',
          zIndex: 10,
          width: groups.length > 0 ? '95%' : '100%'
        }}>
          <div style={{ padding: '0.5rem' }}>
            <input
              autoFocus
              type='text'
              name='group-search-query'
              placeholder='Group number or/and name'
              onChange={(e) => setGroupSearchQuery(e.target.value)}
              value={groupSearchQuery}
              style={{
                border: '1px solid #eaeaea',
                borderRadius: '0.5rem',
                width: '100%',
                height: '40px',
                padding: '0.5rem',
                zIndex: 1
              }}
            />
          </div>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          marginTop: '3rem',
          padding: '0.5rem'
        }}>
          {groups.length === 0 && !isPending && (
            <div style={{
              padding: '1rem',
              textAlign: 'center',
              color: '#4b5563'
            }}>
              {debouncedGroupSearch?.trim() ? 'No groups found' : 'Type to search groups'}
            </div>
          )}

          {isPending && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                color: '#6b7280',
                fontSize: '0.875rem'
              }}>Loading groups...</span>
            </div>
          )}

          {groups.length > 0 && !isPending && groups.map((group, index) => {
            const isSelected = props.value.includes(group.number)

            return (
              <div
                key={group.number}
                style={{
                  padding: '0.5rem',
                  borderBottom: index < groups.length - 1 ? '1px solid #eaeaea' : 'none'
                }}
              >
                <label style={{
                  fontSize: '0.785rem',
                  display: 'grid',
                  gridTemplateColumns: '1rem auto',
                  gridAutoFlow: 'column',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  position: 'relative'
                }}>
                  <input
                    type='checkbox'
                    checked={isSelected}
                    onChange={() => props.onChange(isSelected ? props.value.filter(value => value !== group.number) : [...props.value, group.number])}
                    className='groups-filter-checkbox'
                    style={{
                      appearance: 'none',
                      backgroundColor: isSelected ? '#7728ff' : '#fff',
                      margin: 0,
                      font: 'inherit',
                      color: '#888',
                      width: '1rem',
                      height: '1rem',
                      border: isSelected ? 'transparent' : '2px solid #888',
                      borderRadius: '0.15rem',
                      display: 'grid',
                      placeContent: 'center',
                      cursor: 'pointer',
                      transition: 'border-color 100ms ease-in-out, background-color 100ms ease-in-out',
                      position: 'relative'
                    }}
                  />

                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      fontWeight: '500'
                    }}>
                      {group.number}
                    </span>

                    <span style={{
                      color: '#9ca3af'
                    }}>
                      {group.name}
                    </span>
                  </div>
                </label>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

export default PortalMobileUsers
